<template>
  <div>
    <blogNav />
    <main class="main-container">
      <!-- Header -->
      <div class="header">
        <h1 class="main-title">Kit Presse</h1>
        <p class="subtitle">Ressources pour une présentation uniforme et professionnelle de la marque Query Juriste</p>
      </div>

      <!-- Brand Guidelines -->
      <section class="brand-section">
        <div class="brand-card">
          <h2 class="section-title">Directives de marque</h2>
          <p class="section-description">
            Ressources pour maintenir une présentation uniforme et professionnelle de la marque Query Juriste.
            Pour toute demande, contactez etienneleguay@query-juriste.com.
          </p>
          <a class="download-button" href="/assets/brand/Logos/logos.zip" download>
            <span>Télécharger les ressources de marque</span>
            <svg class="download-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1V11M8 11L11 8M8 11L5 8M2 13H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </section>

      <!-- About Query Juriste -->
      <section class="about-section">
        <h2 class="section-title">À propos de Query Juriste</h2>
        <p class="about-description">
          Query Juriste est une IA spécialisée dans le domaine juridique et les services professionnels. Les cabinets d'avocats
          et entreprises utilisent Query Juriste pour une prise de décision plus rapide et surtout, plus intelligente.
          Basé Station F, Query Juriste compte plus de 1000 utilisateurs.
        </p>
      </section>

      <!-- What Query Juriste Does -->
      <section class="features-section">
        <h2 class="section-title">Ce que fait Query Juriste</h2>
        <p class="section-description">
          Query Juriste est une IA que vous pouvez interroger sur des questions juridiques complexes ou des cas concrets.
          Elle ne répond pas de manière instantanée&nbsp;: un avis prend généralement <strong>5 à 10 minutes</strong>.
          Sa valeur ajoutée réside dans la manière dont elle traite les documents et structure son raisonnement,
          ce qui lui confère une capacité de raisonnement juridique particulièrement rigoureuse.
        </p>
      </section>

      <!-- How it Works -->
      <section class="how-section">
        <h2 class="section-title">Comment fonctionne Query Juriste</h2>
        <p class="section-description">
          Query Juriste utilise une expertise métier pour convertir les processus professionnels en agents IA
          de haute qualité qui produisent un travail de niveau expert. Notre IA ne se contente pas de répondre,
          elle raisonne comme un juriste expérimenté, structure ses analyses et vérifie ses sources.
          Hébergée en France chez Scaleway, Query Juriste garantit une conformité RGPD totale et la souveraineté des données.
        </p>
      </section>

      <!-- Logos Section -->
      <section class="logos-section">
        <div class="logo-section">
          <h2 class="section-title">Logotype</h2>
          <p class="logo-description">
            Le logotype Query Juriste est l'identifiant visuel privilégié pour toutes les annonces de partenariat,
            matériels de presse et références médias.
          </p>
          <div class="logo-grid">
            <div class="logo-item">
              <div class="logo-preview logo-white">
                <div class="logo-placeholder">Logo Blanc</div>
              </div>
              <a class="logo-download" href="/logo-blanc.png" download>Télécharger Logo Blanc</a>
            </div>
            <div class="logo-item">
              <div class="logo-preview logo-black">
                <div class="logo-placeholder">Logo Noir</div>
              </div>
              <a class="logo-download" href="/logo-noir.png" download>Télécharger Logo Noir</a>
            </div>
          </div>
        </div>

        <div class="logomark-section">
          <h2 class="section-title">Logo avec background</h2>
          <p class="logo-description">
            Le logo Query Juriste avec background coloré est réservé aux instances où les contraintes d'espace
            nécessitent un identifiant visuel plus compact.
          </p>
          <div class="logo-grid">
            <div class="logo-item">
              <div class="logo-preview logo-colored">
                <div class="logo-placeholder">Logo avec Background</div>
              </div>
              <a class="logo-download" href="/logo-purple.jpg" download>Télécharger Logo Coloré</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <div class="cta-demo-section">
      <h3 class="highlight text-xl md:text-2xl font-semibold tracking-tight">Query Juriste, l'IA juridique qui raisonne</h3>
      <a
        href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
        @click.prevent="openCalendlyDemo"
        class="cta-button"
      >
        <span style="font-weight: 500;">Réserver une démo</span>
        <svg class="arrow-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    </div>

    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="/logo-query-juriste-png.png" alt="Query Juriste" class="logo" />
          <p class="footer-description">
            L'IA juridique qui raisonne avec vos besoins.
            Simplifiez vos recherches juridiques.
          </p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>Pages</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/">Accueil</a></li>
              <li><a href="https://www.query-juriste.com/blog">Blog</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Produit</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/loginPage">Connexion</a></li>
              <li><a href="https://www.query-juriste.com/contactPage">Contact</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/mentionsPage">Mentions légales</a></li>
              <li><a href="https://www.query-juriste.com/conditions-utilisation">CGU</a></li>
              <li><a href="https://www.query-juriste.com/conditions-vente">CGV</a></li>
              <li><a href="https://www.query-juriste.com/politique-donnees">Confidentialité</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© 2025 Query Juriste, tous droits réservés</p>
      </div>
    </footer>
  </div>
</template>


<script>
import BlogNav from "@/components/BlogNav.vue"
import { openCalendlyDemo } from "@/utils/calendly";

export default {
  name: "PressPage",
  components: {
    BlogNav,
  },
  methods: {
    openCalendlyDemo,
  },
  metaInfo() {
    return {
      title: "Kit Presse – Query Juriste",
      meta: [
        {
          name: "description",
          content:
            "Ressources presse et identité de marque Query Juriste. Téléchargez logos, découvrez notre mission et nos fonctionnalités IA pour la recherche juridique.",
        },
      ],
      link: [{ rel: "canonical", href: "https://www.query-juriste.com/pressPage" }],
    };
  },
};
</script>

<style scoped>
/* Main Container */
.main-container {
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  width: 70%;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Header */
.header {
  text-align: center;
  padding: 3rem 0 4rem;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 300;
  color: #111827;
  margin: 0 0 1rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 1.125rem;
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* Section Styling */
.brand-section,
.about-section,
.features-section,
.how-section,
.logos-section {
  margin-bottom: 4rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
}

.section-description {
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 2rem;
}

/* Brand Card */
.brand-card {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.download-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  background: linear-gradient(135deg, #625CFB 0%, #4F46E5 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px 0 rgba(98, 92, 251, 0.25);
  border: none;
  cursor: pointer;
}

.download-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(98, 92, 251, 0.35);
}

.download-icon {
  transition: transform 0.2s ease;
}

.download-button:hover .download-icon {
  transform: translateY(2px);
}

/* About Section */
.about-description {
  font-size: 1.125rem;
  line-height: 1.7;
  color: #374151;
}

/* Features List */
.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.features-list li {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border-left: 4px solid #625CFB;
  line-height: 1.6;
}

.features-list strong {
  color: #625CFB;
}

/* Logo Sections */
.logo-section,
.logomark-section {
  margin-bottom: 3rem;
}

.logo-description {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.logo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.logo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.logo-preview {
  width: 200px;
  height: 120px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e5e7eb;
  position: relative;
  overflow: hidden;
}

.logo-white {
  background: #ffffff;
}

.logo-black {
  background: #111827;
}

.logo-colored {
  background: linear-gradient(135deg, #625CFB 0%, #4F46E5 100%);
}

.logo-placeholder {
  font-size: 0.875rem;
  font-weight: 500;
  color: #9ca3af;
}

.logo-black .logo-placeholder,
.logo-colored .logo-placeholder {
  color: #ffffff;
}

.logo-download {
  padding: 0.5rem 1rem;
  background: transparent;
  color: #625CFB;
  border: 1px solid #625CFB;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
}

.logo-download:hover {
  background: #625CFB;
  color: white;
  transform: translateY(-1px);
}

/* CTA Demo Section */
.cta-demo-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  margin: 4rem 0 2rem;
  border-radius: 1rem;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  max-width: none;
}

.highlight {
  background: linear-gradient(90deg, #000000 0%, #625CFB 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  background: linear-gradient(135deg, #625CFB 0%, #4F46E5 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px 0 rgba(98, 92, 251, 0.25);
  position: relative;
  overflow: hidden;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #7C3AED 0%, #6366F1 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.cta-button:hover::before {
  opacity: 1;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(98, 92, 251, 0.35);
}

.cta-button span,
.arrow-icon {
  position: relative;
  z-index: 1;
}

.arrow-icon {
  transition: transform 0.2s ease;
}

.cta-button:hover .arrow-icon {
  transform: translateX(2px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .brand-card {
    padding: 1.5rem;
  }

  .logo-grid {
    grid-template-columns: 1fr;
  }

  .cta-demo-section {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    text-align: center;
  }

  .highlight {
    margin-bottom: 0;
    font-size: 1.25rem;
  }

  .cta-button {
    width: 100%;
    justify-content: center;
  }
}

/* Footer (same as original) */
.footer {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 3rem 0 0 0;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  align-items: start;
}

.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-logo-section .logo {
  height: 60px;
  width: 60px;
  align-items: center;
  margin-bottom: 0.5rem;
}

.footer-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.footer-links-grid {
  display: flex;
  gap: 3rem;
}

.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-column li {
  margin: 0;
}

.footer-column a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  line-height: 1.4;
}

.footer-column a:hover {
  color: #625CFB;
}

.footer-bottom {
  margin-top: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.footer-bottom p {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Footer responsive */
@media (max-width: 1024px) {
  .footer-links-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .footer-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
    text-align: center;
  }

  .footer-logo-section {
    align-items: center;
  }

  .footer-description {
    text-align: center;
    max-width: none;
  }

  .footer-links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .footer-column {
    text-align: left;
  }

  .footer-bottom p {
    padding: 0 1rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .footer-links-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .footer-column {
    text-align: center;
  }
}
</style>
